<script lang="ts">
  import { HORIZON_CEILINGS } from '../../core/balance';
  import { ceiling, floor, isVisited, medals } from '../../core/engine';
  import { CATEGORY_LABELS, FAMOUS_NUMBERS, type FamousCategory } from '../../core/famous';
  import { isTwinPrime, primesBetween, primesUpTo } from '../../core/numbers';
  import Icon from '../../shared/components/Icon.svelte';
  import { fmt } from '../../shared/lib/format';
  import { game } from '../../shared/lib/game.svelte';

  const BLOCK = 1000;
  const TOTAL_PRIMES = primesUpTo(HORIZON_CEILINGS.at(-1)!);

  let tab = $state<'primes' | 'famous'>('primes');

  const s = $derived(game.s);
  const earned = $derived(medals(s));
  const inReach = $derived(primesUpTo(ceiling(s)));

  // Prime grid: one block of 1,000 integers at a time, starting with the player's current block.
  const lastBlock = $derived(Math.floor((ceiling(s) - 1) / BLOCK));
  let block = $state(
    Math.min(
      Math.max(0, Math.floor(game.s.maxVisited / BLOCK)),
      Math.floor((ceiling(game.s) - 1) / BLOCK),
    ),
  );
  const lo = $derived(block * BLOCK + 1);
  const hi = $derived(Math.min((block + 1) * BLOCK, ceiling(s)));
  const tiles = $derived(primesBetween(lo, hi));
  const blockEarned = $derived(tiles.filter((p) => p <= s.maxVisited).length);

  const categories = (Object.keys(CATEGORY_LABELS) as FamousCategory[]).map((c) => ({
    id: c,
    label: CATEGORY_LABELS[c],
    items: FAMOUS_NUMBERS.filter((f) => f.category === c).sort((a, b) => a.n - b.n),
  }));
  const famousFound = $derived(FAMOUS_NUMBERS.filter((f) => isVisited(s, f.n)).length);
</script>

<div class="view">
  <header class="view-head">
    <h1>Collection</h1>
    <p>
      Every prime you reach is a medal. Famous numbers reveal their stories when you land on them.
    </p>
  </header>

  <div class="seg" role="tablist" aria-label="Collection">
    <button
      role="tab"
      aria-selected={tab === 'primes'}
      class:on={tab === 'primes'}
      onclick={() => (tab = 'primes')}
    >
      Prime medals <span class="num">{fmt(earned)}</span>
    </button>
    <button
      role="tab"
      aria-selected={tab === 'famous'}
      class:on={tab === 'famous'}
      onclick={() => (tab = 'famous')}
    >
      Famous numbers <span class="num">{famousFound}/{FAMOUS_NUMBERS.length}</span>
    </button>
  </div>

  {#if tab === 'primes'}
    <section class="summary">
      <div class="figure">
        <span class="big num">{fmt(earned)}</span>
        <span class="of"
          >of {fmt(inReach)} primes within your horizon · {fmt(TOTAL_PRIMES)} in the whole game</span
        >
      </div>
      <div class="progress" aria-hidden="true">
        <span style:transform="scaleX({inReach ? earned / inReach : 0})"></span>
      </div>
    </section>

    <section class="block">
      <div class="block-nav">
        <button
          class="icon-btn"
          onclick={() => block--}
          disabled={block === 0}
          aria-label="Previous block"
        >
          <Icon name="left" />
        </button>
        <div class="range">
          <span class="num">{fmt(lo)}–{fmt(hi)}</span>
          <small class="num">{blockEarned} / {tiles.length}</small>
        </div>
        <button
          class="icon-btn"
          onclick={() => block++}
          disabled={block >= lastBlock}
          aria-label="Next block"
        >
          <Icon name="right" />
        </button>
      </div>

      <ol class="tiles">
        {#each tiles as p (p)}
          {@const got = p <= s.maxVisited}
          <li
            class="tile num"
            class:got
            class:twin={isTwinPrime(p)}
            class:here={p === s.position}
            title={got ? `${fmt(p)} — earned` : `${fmt(p)} — not reached yet`}
          >
            {fmt(p)}
          </li>
        {/each}
      </ol>
      <p class="legend">
        <span><i class="sw got"></i>Earned</span>
        <span><i class="sw twin"></i>Twin prime</span>
        <span><i class="sw"></i>Ahead of you</span>
      </p>
    </section>
  {:else}
    {#each categories as c (c.id)}
      <section class="famous" aria-labelledby="c-{c.id}">
        <h2 id="c-{c.id}" class="eyebrow">{c.label}</h2>
        <ul>
          {#each c.items as f (f.id)}
            {@const found = isVisited(s, f.n)}
            {@const outside = f.n > ceiling(s) || f.n < floor(s)}
            <li class:found>
              <span class="n num">{fmt(f.n)}</span>
              <div class="txt">
                <strong>{found ? f.name : 'Undiscovered'}</strong>
                <span>
                  {#if found}{f.desc}
                  {:else if f.n < 0 && s.upgrades.inverter === 0}Below zero. The Inverter can take
                    you there.
                  {:else if outside}Beyond your horizon for now.
                  {:else}Walk to {fmt(f.n)} to reveal it.{/if}
                </span>
              </div>
            </li>
          {/each}
        </ul>
      </section>
    {/each}
  {/if}
</div>

<style>
  .seg {
    display: flex;
    padding: 4px;
    gap: 4px;
    border-radius: 999px;
    background: var(--bg-sunken);
    align-self: flex-start;
    max-width: 100%;
  }
  .seg button {
    display: inline-flex;
    gap: var(--space-2);
    align-items: center;
    padding: 8px 16px;
    border-radius: 999px;
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-2);
    white-space: nowrap;
    transition:
      background-color var(--t) var(--ease-out),
      color var(--t) var(--ease-out);
  }
  .seg button .num {
    font-size: var(--text-xs);
    color: var(--ink-3);
  }
  .seg button.on {
    background: var(--bg-raised);
    color: var(--ink);
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.12);
  }

  .summary {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .figure {
    display: flex;
    align-items: baseline;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .big {
    font-family: var(--font-num);
    font-weight: var(--num-weight);
    font-size: 3rem;
    line-height: 1;
    color: var(--prime);
  }
  .of {
    color: var(--ink-2);
    font-size: var(--text-sm);
  }
  .progress {
    height: 6px;
    border-radius: 3px;
    background: var(--bg-sunken);
    overflow: hidden;
  }
  .progress span {
    display: block;
    height: 100%;
    background: var(--prime);
    transform-origin: left;
    transition: transform var(--t-slow) var(--ease-out);
  }

  .block {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .block-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  .range {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: 600;
  }
  .range small {
    color: var(--ink-3);
    font-weight: 500;
  }
  .icon-btn {
    width: 40px;
    height: 40px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    border: 1px solid var(--line);
    background: var(--bg-raised);
    color: var(--ink-2);
  }
  .icon-btn:disabled {
    opacity: 0.35;
  }

  .tiles {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));
    gap: 6px;
  }
  .tile {
    display: grid;
    place-items: center;
    height: 36px;
    border-radius: var(--radius-sm);
    font-size: var(--text-xs);
    font-weight: 600;
    color: var(--ink-3);
    border: 1px dashed var(--line);
  }
  .tile.got {
    border: 1px solid color-mix(in srgb, var(--prime) 45%, transparent);
    background: color-mix(in srgb, var(--prime) 14%, var(--bg-raised));
    color: var(--prime);
    animation: earn var(--t-slow) var(--ease-spring) backwards;
  }
  .tile.got.twin {
    border-color: color-mix(in srgb, var(--twin) 55%, transparent);
    background: color-mix(in srgb, var(--twin) 14%, var(--bg-raised));
    color: var(--twin);
  }
  .tile.here {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  @keyframes earn {
    from {
      transform: scale(0.85);
      opacity: 0;
    }
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-4);
    font-size: var(--text-xs);
    color: var(--ink-3);
  }
  .legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .sw {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 1px dashed var(--line);
  }
  .sw.got {
    border: 0;
    background: var(--prime);
  }
  .sw.twin {
    border: 0;
    background: var(--twin);
  }

  .famous {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .famous ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
    gap: var(--space-2);
  }
  .famous li {
    display: flex;
    gap: var(--space-4);
    align-items: flex-start;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius);
    border: 1px dashed var(--line);
    min-width: 0;
  }
  .famous li.found {
    border: 1px solid var(--line);
    background: var(--bg-raised);
  }
  .n {
    font-family: var(--font-num);
    font-weight: var(--num-weight);
    font-size: var(--text-lg);
    min-width: 3.4ch;
    color: var(--ink-3);
    line-height: 1.3;
  }
  .found .n {
    color: var(--famous);
  }
  .txt {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-size: var(--text-sm);
  }
  .txt strong {
    font-weight: 650;
  }
  .txt span {
    color: var(--ink-2);
  }
  li:not(.found) .txt strong {
    color: var(--ink-3);
  }
</style>
